import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, Calendar, Loader2 } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { translations } from "../i18n/translations";
import { BLOG_CATEGORIES } from "../data/mock";
import { fetchPosts, imgUrl } from "../lib/api";
import Seo from "../components/Seo";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";

const formatDate = (d, lang) =>
  new Date(d).toLocaleDateString(lang === "tr" ? "tr-TR" : "en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

const Blog = () => {
  const { lang } = useLanguage();
  const t = translations[lang].blogPage;
  const [active, setActive] = useState("all");
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    fetchPosts()
      .then((data) => {
        if (mounted) setPosts(data);
      })
      .catch((e) => console.error(e))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  const filtered = useMemo(
    () => (active === "all" ? posts : posts.filter((p) => p.category === active)),
    [active, posts]
  );

  const featured = posts[0];

  return (
    <>
      <Seo title={t.hero} description={t.heroDesc} path="/blog" lang={lang} />
      <PageHero title={t.hero} desc={t.heroDesc} />

      <section className="bg-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          {loading ? (
            <div className="flex items-center justify-center py-20 text-slate-400">
              <Loader2 className="w-8 h-8 animate-spin" />
            </div>
          ) : (
            <>
              {/* Featured */}
              {featured && (
                <Reveal>
                  <Link
                    to={`/blog/${featured.slug}`}
                    className="group grid lg:grid-cols-2 gap-8 rounded-3xl overflow-hidden border border-slate-200 hover:shadow-xl transition-shadow mb-14"
                  >
                    <div className="relative overflow-hidden h-64 lg:h-auto">
                      <img
                        src={imgUrl(featured.image)}
                        alt={featured.title[lang]}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-8 lg:p-10 flex flex-col justify-center">
                      <span className="inline-flex w-fit items-center rounded-full bg-amber-500/15 text-amber-600 text-xs font-semibold px-3 py-1">
                        {BLOG_CATEGORIES.find((c) => c.id === featured.category)?.label[lang] || featured.category}
                      </span>
                      <h2 className="mt-4 font-display font-bold text-2xl lg:text-3xl text-navy-900 leading-tight group-hover:text-amber-600 transition-colors">
                        {featured.title[lang]}
                      </h2>
                      <p className="mt-4 text-slate-600 leading-relaxed">{featured.excerpt[lang]}</p>
                      <div className="mt-6 flex items-center gap-5 text-sm text-slate-500">
                        <span className="flex items-center gap-1.5">
                          <Calendar className="w-4 h-4" /> {formatDate(featured.date, lang)}
                        </span>
                        <span className="flex items-center gap-1.5">
                          <Clock className="w-4 h-4" /> {featured.read_time} {t.readTime}
                        </span>
                      </div>
                    </div>
                  </Link>
                </Reveal>
              )}

              {/* Filter */}
              <div className="flex flex-wrap gap-3 mb-10">
                {BLOG_CATEGORIES.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => setActive(c.id)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      active === c.id
                        ? "bg-navy-900 text-white"
                        : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    }`}
                  >
                    {c.label[lang]}
                  </button>
                ))}
              </div>

              {/* Grid */}
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {filtered.map((p, i) => (
                  <Reveal key={p.id || p.slug} delay={(i % 3) * 90}>
                    <Link
                      to={`/blog/${p.slug}`}
                      className="group block h-full rounded-2xl overflow-hidden border border-slate-200 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                    >
                      <div className="relative overflow-hidden h-52">
                        <img
                          src={imgUrl(p.image)}
                          alt={p.title[lang]}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <span className="absolute top-4 left-4 inline-flex items-center rounded-full bg-white/90 backdrop-blur text-navy-900 text-xs font-semibold px-3 py-1">
                          {BLOG_CATEGORIES.find((c) => c.id === p.category)?.label[lang] || p.category}
                        </span>
                      </div>
                      <div className="p-6">
                        <div className="flex items-center gap-4 text-xs text-slate-500">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" /> {formatDate(p.date, lang)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" /> {p.read_time} {t.readTime}
                          </span>
                        </div>
                        <h3 className="mt-3 font-display font-bold text-lg text-navy-900 leading-snug group-hover:text-amber-600 transition-colors">
                          {p.title[lang]}
                        </h3>
                        <p className="mt-3 text-slate-600 text-sm leading-relaxed line-clamp-3">
                          {p.excerpt[lang]}
                        </p>
                        <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-amber-600 group-hover:gap-2.5 transition-all">
                          {t.readMore} <ArrowRight className="w-4 h-4" />
                        </span>
                      </div>
                    </Link>
                  </Reveal>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </>
  );
};

export default Blog;
